import React, { useCallback, useEffect, useRef, useState } from 'react';
import Layout from '~/components/Layout';
import { useTranslation } from 'react-i18next';
import { Dimmer, Divider, Dropdown, Input, Loader, Message } from 'semantic-ui-react';
import { StyledFolderWrapper, StyledLeftSideWrapper } from './style';
import notify from '~/components/Notify';
import { CloudStorageService } from '~/api/services/cloudStorage';
import FilesLayout from './FilesLayout'
import FarmerGroupsService from '~/api/services/farmerGroups';
import { El } from '~/components/Layout/style';
import Dialog from '~/components/Dialog';
import { ADMIN_ROLE_SLUG, FARMER_ROLE_SLUG, OPERATIVE_ROLE_SLUG } from '~/Constants';
import { useAuth } from '~/routes/components/AuthProvider';
import { useDebounce } from 'use-lodash-debounce';
import cloneDeep from 'lodash.clonedeep'
import { sortAlphabetically } from '~/utils/helpers';
import Folders from './Folders';
import CreateEditFolder from './CreateEditFolder';

const CloudStorage: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const { systemData: { logged: { roleSlug } } } = useAuth()
  const [isBusy, setIsBusy] = useState(false);
  const editingRole = (roleSlug === ADMIN_ROLE_SLUG) || (roleSlug === OPERATIVE_ROLE_SLUG)
  const farmerRole = roleSlug === FARMER_ROLE_SLUG
  
  const [openCreateFolderModal, setOpenCreateFolderModal] = useState(false)
  const [folders, setFolders] = useState([])
  const [optionsData, setOptionsData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [deleteFolder, setDeleteFolder] = useState(null)
  const [newFolderFiles, setNewFolderFiles] = useState([])
  const [dataFiltered, setDataFiltered] = useState({
    type: 'all',
    page: 0,
    rows: 10,
    column: ''
  })
  const [total, setTotal] = useState(0)
  const [folderId, setFolderId] = useState('')
  const [hasMoreFolders, setHasMoreFolders] = useState(true)
  const [editFolderId, setEditFolderId] = useState(null)

  const debouncedValue = useDebounce(search, 300);
  const hasMoreRef = useRef(null)
  const dateFilteredRef = useRef({
    type: 'all',
    page: 0,
    rows: 10,
    column: ''
  })

  const options = [
    { key: 1, text: t('Created at'), value: 'createdAt', icon: 'sort content ascending' },
    { key: 2, text: t('Alphabetic'), value: 'alphabetic', icon: 'sort alphabet ascending' },
    { key: 3, text: t('Default'), value: 'none', icon: 'sort' }
  ];

  useEffect(() => {
    fetchData()
  }, [t])

  useEffect(() => {
    hasMoreRef.current = hasMoreFolders
  }, [hasMoreFolders])

  useEffect(() => {
    if (folderId && dateFilteredRef?.current?.page !== dataFiltered.page) {
      dateFilteredRef.current = dataFiltered
      fetchNewFolders()
    } else {
      dateFilteredRef.current = dataFiltered
    }
  }, [dataFiltered])

  useEffect(() => {
    if (folderId && !loading) {
      fetchSearchedFolders()
    }
  }, [debouncedValue])

  const handleRect = useCallback((node) => {
    if (node) {
      const observer = new IntersectionObserver(
        entries => {
          const firstEntry = entries[0];
          if (firstEntry.isIntersecting && hasMoreRef.current && search === '') {
            setDataFiltered({ ...dateFilteredRef.current, page: dateFilteredRef.current.page + 1 })
          }
        },
        { root: node.parent, threshold: 1 }
      );
      observer.observe(node);
      return () => observer.disconnect();
    }
  }, [hasMoreRef]);

  async function fetchSearchedFolders() {
    setLoading(true);
    try {
      const folderResponse = await CloudStorageService.getCloudStorageFolders({
        page: 0,
        search: debouncedValue
      })
      setHasMoreFolders(folderResponse.data?.rows?.length < folderResponse.data.filtered)
      setFolders(folderResponse.data.rows)
    } catch (error) {
      if (error?.response?.data) {
        notify(error?.response?.data?.message, 'error');
      } else {
        notify(t('General error'), 'error');
      }
    }
    setLoading(false);
  }

  async function fetchNewFolders() {
    setLoading(true);
    try {
      const folderResponse = await CloudStorageService.getCloudStorageFolders({
        ...dataFiltered,
        folderId,
        column: dataFiltered.column === 'alphabetic' ? '' : dataFiltered.column
      })
      const array = dataFiltered.column === 'alphabetic' ? sortAlphabetically(cloneDeep([...folders, ...folderResponse.data.rows])) :
        [...folders, ...folderResponse.data.rows]
      setFolders(array)
      setHasMoreFolders(array.length < total)
    } catch (error) {
      if (error?.response?.data) {
        notify(error?.response?.data?.message, 'error');
      } else {
        notify(t('General error'), 'error');
      }
    }
    setLoading(false);
  }

  async function fetchData() {
    setIsBusy(true);
    try {
      const folderResponse = await CloudStorageService.getCloudStorageFolders({ search, page: dataFiltered.page, rows: 10 })
      setFolders(folderResponse.data.rows)
      if (folderResponse.data.rows.length > 0) {
        const folderId = farmerRole ? 'newFolder' : folderResponse.data?.rows[0]?.cloudStorageFolderId
        setFolderId(folderId)
      }
      setTotal(folderResponse.data.total)
      setHasMoreFolders(folderResponse.data.rows.length < folderResponse.data.total)
      if (editingRole) {
        const responseOptions = await CloudStorageService.getCloudStorageOptions()
        const responseGroups = await FarmerGroupsService.getFarmerGroupsList('cloud_storage')
        setOptionsData({ ...responseOptions.data, farmerGroups: responseGroups.data })
      }
    } catch (error) {
      if (error?.response?.data) {
        notify(error?.response?.data?.message, 'error');
      } else {
        notify(t('General error'), 'error');
      }
    }
    setIsBusy(false);
  }

  async function handleSortFoldersByTime() {
    setLoading(true)
    try {
      dateFilteredRef.current = { ...dataFiltered, page: 0, rows: 10, column: 'created_at' }
      const folderResponse = await CloudStorageService.getCloudStorageFolders({ ...dataFiltered, search, page: 0, rows: 10, column: 'created_at' })
      setFolders(folderResponse.data.rows)
      setHasMoreFolders(folderResponse.data?.rows?.length < folderResponse.data.filtered)
      setDataFiltered({ ...dataFiltered, page: 0, rows: 10, column: 'created_at' })
    } catch (error) {
      if (error?.response?.data) {
        notify(error?.response?.data?.message, 'error');
      } else {
        notify(t('General error'), 'error');
      }
    }
    setLoading(false);
  }

  async function handleSortFoldersByNone() {
    setLoading(true)
    try {
      dateFilteredRef.current = { ...dataFiltered, page: 0, rows: 10, column: '' }
      const folderResponse = await CloudStorageService.getCloudStorageFolders({ ...dataFiltered, search, page: 0, rows: 10, column: '' })
      setFolders(folderResponse.data.rows)
      setHasMoreFolders(folderResponse.data?.rows?.length < folderResponse.data.filtered)
      setDataFiltered({ ...dataFiltered, page: 0, rows: 10, column: '' })
    } catch (error) {
      if (error?.response?.data) {
        notify(error?.response?.data?.message, 'error');
      } else {
        notify(t('General error'), 'error');
      }
    }
    setLoading(false);
  }

  function handleAlphabeticSort() {
    setDataFiltered({ ...dataFiltered, column: 'alphabetic' })
    const arrayFolders = sortAlphabetically(cloneDeep(folders))
    setFolders(arrayFolders)
  }

  function handleClickSort(typeSort) {
    switch (typeSort) {
      case 'createdAt':
        return handleSortFoldersByTime()
      case 'alphabetic':
        return handleAlphabeticSort()
      case 'none':
        return handleSortFoldersByNone()
      default:
        return
    }
  }

  function renderSearch() {
    return <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-around' }}>
      <Dropdown icon={'bars'} text={''} selectOnBlur={false}>
        <Dropdown.Menu style={{ zIndex: 9999 }}>
          {options.map(option => {
            return <Dropdown.Item
              onClick={() => handleClickSort(option.value)}
              icon={option.icon}
              content={option.text}
              key={option.key}
            />
          })}
        </Dropdown.Menu>
      </Dropdown>
      <Input
        icon={{ name: 'search', circular: true, link: true }}
        placeholder={t('Search folders...')}
        style={{ marginBottom: 10 }}
        value={search}
        onChange={(e, data) => setSearch(data.value)}
      />
    </div>

  }

  function renderFolders() {
    return <Folders
      folderId={folderId}
      folders={folders}
      setDeleteFolder={setDeleteFolder}
      newFolderFiles={newFolderFiles}
      handleEditFolder={(folder) => {
        setEditFolderId(folder.id)
        setOpenCreateFolderModal(true)
      }}
      column={dataFiltered.column}
      handleRect={handleRect}
      setFolderId={setFolderId}
    />
  }

  function renderNoResultFolders() {
    return <Message info style={{ marginTop: '20px', textAlign: 'center' }}>
      <Message.Header>{t('No results')}</Message.Header>
      <p>{t('Sorry, there are no folders to display.')}</p>
    </Message>
  }

  function renderLeftSide() {
    return !isBusy && <StyledLeftSideWrapper>
      {renderSearch()}
      <Divider />
      <StyledFolderWrapper>
        <div style={{ position: 'relative' }}>
          <Dimmer inverted active={loading}>
            <Loader active={loading} />
          </Dimmer>
          {folders.length > 0 ? renderFolders() : renderNoResultFolders()}
        </div>
      </StyledFolderWrapper>
    </StyledLeftSideWrapper>
  }

  function renderRightSide() {
    const folder = folders.find(folder => folder.id === folderId) || folderId === 'newFolder'
    const loadFilesLayout = editingRole ? folder && optionsData && dataFiltered.type !== '' : folder && dataFiltered.type !== ''
    return loadFilesLayout && <FilesLayout
      folderName={folder?.title}
      optionsData={optionsData}
      id={folderId}
      type={dataFiltered.type}
      handleChangeFolders={(filesCount, id) => {
        const temp = [...folders]
        const index = folders.findIndex(folder => folder.id === id)
        if (index >= 0) {
          temp[index].filesCount = filesCount
        }
        setFolders(temp)
      }}
    />
  }

  function createEditFolderModal() {
    return <CreateEditFolder
      openCreateFolderModal={openCreateFolderModal}
      handleClose={(folders) => {
        if(folders) {
          setFolders(folders.rows)
        }
        setOpenCreateFolderModal(false)
        setEditFolderId(null)
      }}
      optionsData={optionsData}
      editFolderId={editFolderId}
    />
  }

  function renderActions() {
    return <El.PageHeaderActionButton
      onClick={() => setOpenCreateFolderModal(true)}
    >
      {t('Add new folder')}
    </El.PageHeaderActionButton>
  }

  async function handleDeleteFolder() {
    setLoading(true);
    try {
      await CloudStorageService.deleteFolder(deleteFolder?.id)
      setFolders(folders.filter(folder => folder.id !== deleteFolder?.id))
      setDeleteFolder(null)
      notify(t('Folder successfully deleted'), 'success')
    } catch (error) {
      if (error?.response?.data) {
        notify(error?.response?.data?.message, 'error');
      } else {
        notify(t('General error'), 'error');
      }
    }
    setLoading(false);
  }

  function deleteFolderModal() {
    const folderName = deleteFolder?.title
    return <Dialog
      open={Boolean(deleteFolder)}
      textHeader={t('Delete confirmation')}
      textSubmit={t('Delete')}
      textContent={t('Do you really want to delete folder: {{folderName}}?', { folderName })}
      textClose={t('Cancel')}
      isLoading={loading}
      handleClose={() => setDeleteFolder(null)}
      handleSubmit={() => handleDeleteFolder()}
      type={'abort'}
    />
  }

  return <Layout
    segmented
    isBusy={isBusy}
    title={t('Cloud storage')}
    actions={editingRole && renderActions()}
  >
    {deleteFolderModal()}
    {createEditFolderModal()}
    <div style={{ display: 'flex', gap: 20 }}>
      {renderLeftSide()}
      {renderRightSide()}
    </div>
  </Layout>;
};

export default CloudStorage;
